import useAuth from '@fuse/core/FuseAuthProvider/useAuth';
import FuseLoading from '@fuse/core/FuseLoading';
import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { getSecrets } from '@/utils/secretsManager';

type KalixaStatus = 'success' | 'pending' | 'cancel' | 'error' | 'refused';

const STATUS_MAP: Record<KalixaStatus, 'success' | 'pending' | 'failed'> = {
    success: 'success',
    pending: 'pending',
    cancel: 'failed',
    error: 'failed',
    refused: 'failed',
};

function KalixaReturnPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { authState } = useAuth();
    const isAuthenticated = authState?.isAuthenticated;
    const params = useParams();

    useEffect(() => {
        let canceled = false;

        const processKalixaReturn = async () => {
            const rawStatus = String(params.status || '').toLowerCase() as KalixaStatus;
            const mappedStatus = STATUS_MAP[rawStatus] || 'failed';
            const searchParams = new URLSearchParams(location.search);
            const paymentId = searchParams.get('payment_id');
            const encodedData = searchParams.get('data');

            if (encodedData) {
                try {
                    const { apiURL } = await getSecrets();

                    if (canceled) {
                        return;
                    }

                    const backendReturnUrl = new URL('/portal/billing/kalixa-return/', apiURL);
                    backendReturnUrl.searchParams.set('data', encodedData);
                    window.location.assign(backendReturnUrl.toString());
                    return;
                } catch (error) {
                    console.warn('Kalixa return backend redirect failed, falling back to status redirect.', error);
                }
            }

            const query = new URLSearchParams();
            query.set('postback', 'true');
            query.set('status', mappedStatus);

            if (paymentId) {
                query.set('payment_id', paymentId);
            }

            const billingUrl = `/billing?${query.toString()}`;

            if (isAuthenticated) {
                navigate(billingUrl, { replace: true });
                return;
            }

            const encodedNext = encodeURIComponent(billingUrl);
            navigate(`/sign-in?next=${encodedNext}`, { replace: true });
        };

        void processKalixaReturn();

        return () => {
            canceled = true;
        };
    }, [isAuthenticated, location.search, navigate, params.status]);

    return (
        <div className="flex flex-col items-center justify-center min-h-screen">
            <FuseLoading />
            <div className="mt-4 text-lg text-gray-600">
                Processing Kalixa payment result...
            </div>
        </div>
    );
}

export default KalixaReturnPage;
